import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

import Modal from '@components/_shared/modal';
import Button from '@components/_shared/button';
import styles from '@components/messages/styles.module.scss';
import { useSession } from '@utils/context/session';
import { SignUpButtons } from '@components/authorization/signUpButtons';
import TermsAndPrivacyLinks from '@components/_shared/termsAndPrivacyLinks';
import SignInLink from '@components/authorization/signInLink';
import {
  SegmentEventNamesEnum,
  SegmentEventPagesEnum,
  useAnalytics,
} from '@utils/hooks/useAnalytics';
import { googleSignUp } from '@api/auth/googleSingUp';
import { postCreateConversation } from '@api/messages/postCreateConversation';
import { DatesInputWithListingCalendar } from 'components/messages/_shared/dates-input-with-listing-calendar';
import { ListingType } from '@utils/types/listing';

interface MessageHostModalProps {
  listingTitle: string;
  listingId: string;
  isOpen: boolean;
  onClose: () => void;
  nightlyPrices: ListingType['nightly_prices'];
  disabledDates: string[];
  minNightsStay?: number;
  maxNightsStay?: number;
  firstAvailableDate?: ListingType['first_available_date'];
  lastKnownDay?: ListingType['last_known_date'] | null;
}

const MessageHostModal = (props: MessageHostModalProps) => {
  const {
    listingTitle,
    listingId,
    isOpen,
    onClose,
    disabledDates,
    nightlyPrices,
    minNightsStay,
    maxNightsStay,
    firstAvailableDate,
    lastKnownDay,
  } = props;

  const { user } = useSession();
  const { trackEvent } = useAnalytics();
  const router = useRouter();

  const [data, setData] = useState<{
    number_of_guests: number | '';
    message: string;
    check_in: Date | null;
    check_out: Date | null;
  }>({
    check_in: null,
    check_out: null,
    number_of_guests: 1,
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const onGoogleClick = () => {
    trackEvent(SegmentEventNamesEnum.CONTINUE_WITH_GOOGLE_CLICK, {
      PageName: SegmentEventPagesEnum.LISTING_PAGE,
    });
    googleSignUp(router.asPath, (router.query.token as string) || undefined);
  };

  const onSignUp = () => {
    trackEvent(SegmentEventNamesEnum.SIGNUP_CLICK, {
      PageName: SegmentEventPagesEnum.LISTING_PAGE,
    });
    router.push('/your-info');
  };

  const onLogin = () => {
    trackEvent(SegmentEventNamesEnum.LOGIN_BUTTON_CLICK, {
      PageName: SegmentEventPagesEnum.LISTING_PAGE,
    });
    router.push('/login');
  };

  const onSendClick = () => {
    setLoading(true);
    postCreateConversation({
      message: data.message,
      number_of_guests: data.number_of_guests as number,
      check_in: format(data.check_in!, 'yyyy-MM-dd'),
      check_out: format(data.check_out!, 'yyyy-MM-dd'),
      listing_id: listingId,
    })
      .then((data) => {
        router.push(`/messages?conversation_id=${data?.conversation?.id}`);
        onClose();
        toast.success('Message sent');
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.details
            ? error.response.data.details[0]?.message
            : 'Something went wrong'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!user) {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        containerStyle={styles.chatModal__container}
      >
        <>
          <div className={styles.chatModal__header}>
            Sign up to send a message
          </div>

          <div className={styles.chatModal__divider} />

          <div className={styles.chatModal__content}>
            <span className={styles.chatModal__text}>
              We’re excited for you to join us. Sign up to send a message to the
              host.
            </span>
          </div>

          <div className={styles.chatModal__content}>
            <SignUpButtons
              onGoogleSignUpClick={onGoogleClick}
              onSignUpWithEmailClick={onSignUp}
              containerStyle={styles.chatModal__signUpButtons}
            />

            <TermsAndPrivacyLinks
              containerStyle={styles.chatModal__termsAndPrivacyLinks}
            />

            <SignInLink
              onSignInClick={onLogin}
              containerStyle={styles.chatModal__signInLink}
            />
          </div>
        </>
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      containerStyle={styles.chatModal__container}
    >
      <>
        <div className={styles.chatModal__header}>Send a message</div>

        <div className={styles.chatModal__divider} />

        <div className={styles.chatModal__content}>
          <span className={styles.chatModal__text}>
            Send the owner a message. Please select your expected start and end
            dates.
          </span>
        </div>

        <div className={styles.chatModal__divider} />

        <div className={styles.chatModal__content}>
          <div className={styles.chatModal__fields__item}>
            <label className={styles.chatModal__label} htmlFor={'listing'}>
              Listing
            </label>
            <input
              id={'listing'}
              type="text"
              value={listingTitle}
              readOnly
              className={styles.chatModal__input}
            />
          </div>

          <div className={styles.chatModal__fields__item}>
            <label className={styles.chatModal__label} htmlFor={'check-in'}>
              Dates
            </label>

            <DatesInputWithListingCalendar
              data={data}
              setData={(newData) =>
                setData({
                  ...data,
                  check_in: newData.check_in,
                  check_out: newData.check_out,
                })
              }
              disabledDates={disabledDates}
              minNightsStay={minNightsStay}
              maxNightsStay={maxNightsStay}
              firstAvailableDate={firstAvailableDate}
              lastKnownDay={lastKnownDay}
            />
          </div>

          <div className={styles.chatModal__fields__item}>
            <label className={styles.chatModal__label} htmlFor={'guests'}>
              Guests
            </label>
            <input
              id={'guests'}
              type="number"
              value={data.number_of_guests}
              onChange={(e) =>
                setData({
                  ...data,
                  number_of_guests: e.target.value ? +e.target.value : '',
                })
              }
              placeholder=""
              className={styles.chatModal__input}
            />
          </div>

          <div className={styles.chatModal__fields__item}>
            <label className={styles.chatModal__label} htmlFor={'subtotal'}>
              Message
            </label>
            <textarea
              id={'message'}
              value={data.message}
              onChange={(e) =>
                setData({
                  ...data,
                  message: e.target.value,
                })
              }
              placeholder="I am interested in ..."
              className={styles.chatModal__textarea}
            />
          </div>
        </div>

        <div className={styles.chatModal__content}>
          <div className={styles.chatModal__buttons}>
            <Button
              loading={loading}
              onClick={onSendClick}
              containerStyle={styles.chatModal__button}
              variant="newBlueContained"
              disabled={
                !data.check_in ||
                !data.check_out ||
                !data.number_of_guests ||
                !data.message
              }
            >
              Send Message
            </Button>
          </div>
        </div>
      </>
    </Modal>
  );
};

export default MessageHostModal;
